import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useLikes, likeKeyFromRow } from '../../../shared/contexts/LikesContext'; 
import { PostSection } from './GalleryUIMobileMusicdetail';
import { useTranslation } from 'react-i18next';


export type MediaEntry = {
  kind: 'image' | 'video';

  // image
  url?: string;

  // new video pipeline
  isHls?: boolean;
  videoId?: string | null;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string | null;

  // music
  musicTitle?: string | null;
  musicArtist?: string | null;
  musicId?: string | null;
};

export interface Post {
  _id: string;
  media: MediaEntry[];
  captions: string[];
  createdAt: string;
  likes: number;
  likedByUser?: boolean;

  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;

  location?: {
    city: string;
    country: string;
    countryCode?: string;
  };
  user: {
    _id: string;
    profilename: string;
    picture?: string | null;
  } | null;
  totalLikes?: number;
  likeKey?: string;
  hashtags?: string[];
  status?: string;
}


export default function LocationPostMobile() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { likedIds, likePost, unlikePost } = useLikes();

  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [likingInProgress, setLikingInProgress] = useState<string | null>(null);

  const globalMuted = true;


const handleMusicClick = async (entry: MediaEntry) => {
  const resolvedMusicId =
    entry.musicId ||
    post?.musicId ||
    post?.media?.find((m) => m.musicId)?.musicId ||
    null;

  if (resolvedMusicId) {
    navigate(`/musicdetails/${encodeURIComponent(resolvedMusicId)}`);
    return;
  }

  const title =
    entry.musicTitle ||
    post?.musicTitle ||
    post?.media?.find((m) => m.musicTitle)?.musicTitle ||
    '';

  const artist =
    entry.musicArtist ||
    post?.musicArtist ||
    post?.media?.find((m) => m.musicArtist)?.musicArtist ||
    '';

  if (!title) {
    console.warn('No musicId and no musicTitle found for music navigation', {
      entry,
      post,
    });
    return;
  }

  try {
    const res = await api.get('/music');
    const list = Array.isArray(res.data) ? res.data : [];

    const norm = (s?: string | null) => String(s || '').trim().toLowerCase();

    let found = list.find(
      (track: any) =>
        norm(track.title) === norm(title) &&
        (!artist || norm(track.artist) === norm(artist))
    );

    if (!found) {
      found = list.find((track: any) => norm(track.title) === norm(title));
    }

    if (found?._id) {
      navigate(`/musicdetails/${encodeURIComponent(found._id)}`);
      return;
    }

    console.warn('Music track could not be resolved from title/artist', {
      title,
      artist,
    });
  } catch (err) {
    console.error('Music lookup failed', err);
  }
};

  useEffect(() => {
  if (!postId) return;

  (async () => {
    setLoading(true);
    try {
      const res = await api.get(`/post-location-detail/${encodeURIComponent(postId)}`);
      const raw = res.data;

      const rawMedia = Array.isArray(raw?.media) ? raw.media : [];

      const media: MediaEntry[] = rawMedia
        .map((m: any) => {
          if (!m) return null;

          return {
            kind: m.kind === 'video' || m.isHls ? 'video' : 'image',
            url: m.url,
            isHls: !!m.isHls,
            videoId: m.videoId ?? null,
            hlsUrl: m.hlsUrl ?? null,
            dashUrl: m.dashUrl ?? null,
            posterUrl: m.posterUrl ?? null,
            musicId: m.musicId ?? null,
            musicTitle: m.musicTitle ?? null,
            musicArtist: m.musicArtist ?? null,
          } as MediaEntry;
        })
        .filter(Boolean) as MediaEntry[];

      const likeKey = likeKeyFromRow(raw);

      const mapped: Post = {
        _id: String(raw._id),
        likeKey,
        media,
        captions: Array.isArray(raw.captions) ? raw.captions : [],
        createdAt: raw.createdAt || new Date().toISOString(),
        likes: Number(raw.likes) || Number(raw.totalLikes) || 0,
        totalLikes: Number(raw.totalLikes) || Number(raw.likes) || 0,
        likedByUser: !!raw.likedByUser,

        musicId: raw.musicId ?? null,
        musicTitle: raw.musicTitle ?? null,
        musicArtist: raw.musicArtist ?? null,

        user: raw.user
          ? {
              _id: String(raw.user._id),
              profilename: raw.user.profilename || t('gallery.post.unknownUser'),
              picture: raw.user.picture ?? null,
            }
          : null,
        location: raw.location
          ? {
              city: raw.location.city || t('gallery.unknownLocation'),
              country: raw.location.country || t('gallery.unknownLocation'),
              countryCode: raw.location.countryCode,
            }
          : undefined,
        hashtags: Array.isArray(raw.hashtags) ? raw.hashtags : [],
        status: raw.status,
      };

      setPost(mapped);
    } catch (e) {
      console.error('Failed to load location post detail:', e);
      setPost(null);
    } finally {
      setLoading(false);
    }
  })();
}, [postId, t]);


  const handleLike = async (id: string) => {
    const key = String(post?.likeKey || id);
    if (likingInProgress === key || likedIds.has(key)) return;

    setLikingInProgress(key);
    try {
      const newLikes = await likePost(key);
      setPost((prev) => prev ? {
        ...prev,
        likedByUser: true,
        likes: typeof newLikes === 'number' ? newLikes : prev.likes + 1,
        totalLikes: typeof newLikes === 'number' ? newLikes : (prev.totalLikes ?? prev.likes) + 1,
      } : prev);
    } finally {
      setLikingInProgress(null);
    }
  };

  const handleUnlike = async (id: string) => {
    const key = String(post?.likeKey || id);
    if (likingInProgress === key || !likedIds.has(key)) return;

    setLikingInProgress(key);
    try {
      const newLikes = await unlikePost(key);
      setPost((prev) => prev ? {
        ...prev,
        likedByUser: false,
        likes: typeof newLikes === 'number' ? newLikes : Math.max(0, prev.likes - 1),
        totalLikes: typeof newLikes === 'number' ? newLikes : Math.max(0, (prev.totalLikes ?? prev.likes) - 1),
      } : prev);
    } finally {
      setLikingInProgress(null);
    }
  };


  // UI Render
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000',
        color: '#e6e9f3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {t('musicDetail.loading')}
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000',
        color: '#e6e9f3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        boxSizing: 'border-box',
      }}>
        {t('musicDetail.notFound')}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      background: '#000',
      position: 'relative',
    }}>
      {/* Close button */}
      <button
        onClick={() => navigate(-1)}
        aria-label={t('musicDetail.close')}
        title={t('musicDetail.close')}
        style={closeBtnStyle}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Content */}
      <div style={{
        flexGrow: 1,
        height: '100dvh',
        overflowY: 'hidden',
        padding: 7,
        boxSizing: 'border-box',
      }}>
        <PostSection
          post={post}
          isActive={true}
          inWindow={true}
          globalMuted={globalMuted}
          navigate={navigate}
          onLike={handleLike}
          onUnlike={handleUnlike}
          currentUserId={user?._id}
          onMusicClick={handleMusicClick}
        />
      </div>
    </div>
  );
}

const closeBtnStyle: React.CSSProperties = {
  position: 'fixed',
  top: 14,
  right: 18,
  zIndex: 60,
  width: 34,
  height: 34,
  display: 'grid',
  placeItems: 'center',
  border: 'none',
  borderRadius: 999,
  background:
    'radial-gradient(circle at 0% 0%, rgba(255,255,255,0.12), rgba(10,10,12,0.95))',
  color: '#e6e9f3',
  backdropFilter: 'blur(10px)',
  cursor: 'pointer',
  transition: 'transform 120ms ease, box-shadow 120ms ease',
  boxShadow: '0 8px 18px rgba(0,0,0,0.45)',
};
